import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, type OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterLink,
  RouterOutlet,
} from '@angular/router';
import { EMPTY, type Observable } from 'rxjs';
import { catchError, filter, map } from 'rxjs/operators';

import { isLoggedIn$ } from '@auth/repository/auth.repository';
import type { ItemInfo } from '@core/models/navigation-item.model';
import { PermissionService } from '@core/permissions/permission.service';
import { AppNavigationService } from '@core/services/app-navigation.service';
import { DIALOG_SIZE, HOME_PAGE } from '@shared/constants/ume-constants';

import { NewUserSignupDialogComponent } from '../../routes/dashboard/components/new-user-signup-dialog/new-user-signup-dialog.component';
import { DashboardState } from '../../routes/dashboard/state/dashboard.state';
import { MainNavWrapComponent } from '../main-nav-wrap/main-nav-wrap.component';
import { NavigationComponent } from '../navigation/navigation.component';
import { NavigationItemsState } from '../navigation/state/navigation-items.state';
import { CompanyTypeBasedRoutePipe } from '../pipes/company-type-based-route.pipe';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { UserNotificationGroupsComponent } from '../user-notification-grops/user-notification-groups.component';

@Component({
  selector: 'ume-base-layout',
  templateUrl: './base-layout.component.html',
  styleUrls: ['./base-layout.component.scss'],
  imports: [
    AsyncPipe,
    RouterOutlet,
    MatProgressBar,
    MatMenuModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    RouterLink,
    CompanyTypeBasedRoutePipe,
    ProfileMenuComponent,
    MainNavWrapComponent,
    NavigationComponent,
    UserNotificationGroupsComponent,
  ],
})
export class BaseLayoutComponent implements OnInit {
  private dialog: MatDialog = inject(MatDialog);
  private router: Router = inject(Router);
  private permissions: PermissionService = inject(PermissionService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _dashboardState = inject(DashboardState);
  private appNavService: AppNavigationService = inject(AppNavigationService);
  protected readonly navigationItems = inject(NavigationItemsState).appNavigationItems;
  public helpItems: ItemInfo[] = getHelpItems();
  public isVirtualPremium: boolean = this.permissions.isVirtualPremiumClient();
  public isPremium: boolean = this.permissions.isPremiumClient();
  public isUmeOps: boolean = this.permissions.isUmeOps();
  public $isLoggedIn: Observable<boolean> = isLoggedIn$;
  protected readonly notifications = this._dashboardState.notifications;

  public loading$ = this.appNavService.routerEvents$.pipe(
    filter(
      (e) =>
        e instanceof NavigationStart ||
        e instanceof NavigationEnd ||
        e instanceof NavigationError ||
        e instanceof NavigationCancel
    ),
    map((e) => e instanceof NavigationStart)
  );

  ngOnInit(): void {
    if (!this.isUmeOps && !this.router.url.includes(HOME_PAGE)) {
      this._dashboardState
        .loadNotifications()
        .pipe(
          catchError(() => EMPTY),
          takeUntilDestroyed(this._destroyRef)
        )
        .subscribe();
    }
  }

  public get notUmeOps(): boolean {
    return !this.permissions.isUmeOps();
  }

  public welcomeGuide(): void {
    this.dialog.open(NewUserSignupDialogComponent, {
      width: DIALOG_SIZE.MODAL_MD,
      autoFocus: true,
    });
  }
}

export function getHelpItems(): ItemInfo[] {
  return [
    {
      label: 'ABOUT_UME',
      menuLabel: 'ABOUT',
      name: 'About ume',
      link: 'https://www.ume.solutions/',
      target: '_blank',
      external: true,
      disabled: false,
      disabledReason: null,
      noProfile: false,
      payment: false,
      icon: 'error_outline',
      subItems: [],
      requestUrl: null,
    },
    {
      label: 'FAQ',
      menuLabel: 'ABOUT',
      name: 'FAQ',
      link: '/about/faq',
      disabled: false,
      disabledReason: null,
      noProfile: false,
      payment: false,
      icon: 'help_outline',
      subItems: [],
      requestUrl: null,
      target: null,
    },
  ];
}
